/**
 * Group Management Page (Refactored)
 * Provides overview and management of organization groups with improved organization and reduced complexity
 */

import {
  Box,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreateGroupDialog,
  CreateGroupRequestDialog,
  GroupDetailsDialog,
  GroupSyncDialog,
  SimpleMemberManagementDialog,
  GroupManagementStats,
  GroupManagementTabs
} from '../components/groups';
import { PageLayout } from '../components/ui/PageLayout';
import { usePermissions } from '../hooks/permissions/usePermissions';
import AuthService from '../services/AuthService';
import { log } from '../utils/errors/logger';
import { useMyGroups, useAllGroups } from '../hooks/api/useGroups';
import type { AppGroup } from '../types/group';

// Organization interface removed for single-tenant

interface GroupStats {
  total: number;
  local: number;
  external: number;
  bySource: Record<string, number>;
}

export const GroupManagement: React.FC = () => {
  const { t } = useTranslation();
  const [user] = useState(() => AuthService.getUser());
  const { can } = usePermissions();

  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [memberManagementGroup, setMemberManagementGroup] = useState<AppGroup | null>(null);

  // Check permissions
  const canViewAllGroups = can.canViewAllGroups; // Can see all groups (moderator+)
  const canCreateGroups = can.canCreateGroups;   // Can create groups directly (moderator+)

  // Single-tenant: Use direct hooks without organization context
  const {
    data: allGroupsResponse,
    isLoading: allGroupsLoading,
    error: allGroupsError
  } = useAllGroups(canViewAllGroups);

  const {
    data: myGroupsResponse,
    isLoading: myGroupsLoading,
    error: myGroupsError
  } = useMyGroups();

  // Compute loading and error states (single-tenant simplified)
  const loading = canViewAllGroups ?
    (allGroupsLoading || myGroupsLoading) :
    myGroupsLoading;

  const error = allGroupsError || myGroupsError;

  // Extract groups arrays from paginated responses
  const allGroups = allGroupsResponse?.groups || [];
  const myGroups = myGroupsResponse?.groups || [];

  // Get the appropriate groups list
  const groups = canViewAllGroups ? allGroups : [];
  const displayMyGroups = myGroups || [];

  // Calculate statistics
  const stats: GroupStats = React.useMemo(() => {
    const bySource: Record<string, number> = {};
    let local = 0;
    let external = 0;

    (groups || []).forEach(group => {
      const source = group.source ?? 'unknown';
      bySource[source] = (bySource[source] || 0) + 1;
      if (source === 'local') {
        local++;
      } else {
        external++;
      }
    });

    return {
      total: (groups || []).length,
      local,
      external,
      bySource
    };
  }, [groups]);

  // Event handlers
  const handleSyncGroups = () => {
    setSyncDialogOpen(true);
    log.user.action('open_sync_dialog', 'GroupManagement');
  };

  const handleCreateGroup = () => {
    if (canCreateGroups) {
      // Moderator/Admin: Open direct create dialog
      setCreateDialogOpen(true);
      log.user.action('open_create_group_dialog', 'GroupManagement');
    } else {
      // Standard user: Open request dialog
      setRequestDialogOpen(true);
      log.user.action('open_request_group_dialog', 'GroupManagement');
    }
  };

  const handleGroupClick = (group: AppGroup) => {
    setSelectedGroupId(group.id);
    log.user.action('view_group_details', 'GroupManagement', { groupId: group.id });
  };

  const handleManageMembers = (group: AppGroup) => {
    setMemberManagementGroup(group);
    log.user.action('manage_group_members', 'GroupManagement', { groupId: group.id });
  };

  const handleRefreshGroups = () => {
    log.info('Groups refreshed automatically by TanStack Query');
  };

  // Derive the selected group from the cached lists so it always reflects
  // the latest server state (e.g. after an edit invalidates the list cache).
  const selectedGroup = useMemo<AppGroup | null>(() => {
    if (!selectedGroupId) return null;
    return (
      allGroups.find((g) => g.id === selectedGroupId) ??
      myGroups.find((g) => g.id === selectedGroupId) ??
      null
    );
  }, [selectedGroupId, allGroups, myGroups]);

  // Organization change handler removed for single-tenant

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const showStatsAndTabs = canViewAllGroups; // Simplified for single-tenant

  // Header actions for PageLayout - role-based button display
  const headerActions = canCreateGroups ? (
    <>
      <Button
        variant="outlined"
        startIcon={<SyncIcon />}
        onClick={handleSyncGroups}
        sx={{ mr: 1 }}
      >
        {t('groups.actions.syncGroups')}
      </Button>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleCreateGroup}
      >
        {t('groups.actions.createGroup')}
      </Button>
    </>
  ) : (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleCreateGroup}
    >
      {t('groupRequests.create.requestButton', 'Request Group')}
    </Button>
  );

  return (
    <>
      <PageLayout
        title={t('groups.title')}
        isLoading={loading}
        maxWidth="xl"
        headerActions={headerActions}
      >
        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error instanceof Error ? error.message : String(error)}
          </Alert>
        )}

        {/* Statistics Cards */}
        {showStatsAndTabs && (
          <GroupManagementStats
            stats={stats}
            myGroupsCount={displayMyGroups.length}
          />
        )}

        {/* Groups Content */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <GroupManagementTabs
            hasGroupManagementPermission={canViewAllGroups}
            allGroups={groups}
            myGroups={displayMyGroups}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onGroupClick={handleGroupClick}
            onManageMembers={handleManageMembers}
            selectedOrgId="" // Empty for single-tenant
            showPendingRequestsTab={canViewAllGroups}
            onRequestProcessed={handleRefreshGroups}
          />
        </Box>
      </PageLayout>

      {/* Dialogs */}
      <GroupSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        onSuccess={handleRefreshGroups}
      />

      <CreateGroupDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleRefreshGroups}
      />

      <CreateGroupRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        onSuccess={handleRefreshGroups}
      />

      {selectedGroup && (
        <GroupDetailsDialog
          group={selectedGroup}
          open={Boolean(selectedGroup)}
          onClose={() => setSelectedGroupId(null)}
          onUpdate={handleRefreshGroups}
        />
      )}

      <SimpleMemberManagementDialog
        group={memberManagementGroup}
        open={Boolean(memberManagementGroup)}
        onClose={() => setMemberManagementGroup(null)}
      />
    </>
  );
};
